import React, { useState, useEffect } from 'react';
import {
    User,
    Mail,
    Phone,
    Building,
    Calendar,
    Shield,
    Save,
    Eye,
    EyeOff,
    RefreshCw,
    AlertCircle,
    CheckCircle,
    Edit,
    Camera,
    Clock,
    Badge
} from 'lucide-react';

const EmployeeProfile = ({ user, userData }) => {
    const currentUser = user || userData;

    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);

    // Form states
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [emailSuccess, setEmailSuccess] = useState('');

    // Password change states
    const [showPasswordForm, setShowPasswordForm] = useState(false);
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');
    const [changingPassword, setChangingPassword] = useState(false);

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.zapbooksai.subraps.com/api';

    useEffect(() => {
        if (currentUser?.companyId) {
            fetchProfile();
        }
    }, [currentUser?.companyId]);

    const fetchProfile = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');

            // Add company_id as query parameter
            const response = await fetch(`${API_BASE_URL}/employee/my-profile?company_id=${currentUser.companyId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setProfile(data.data);
                    setEmail(data.data.email || '');
                }
            } else {
                console.error('Failed to fetch profile:', response.status);
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
        } finally {
            setLoading(false);
        }
    };

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const updateEmail = async () => {
        if (!email.trim()) {
            setEmailError('Email is required');
            return;
        }

        if (!validateEmail(email)) {
            setEmailError('Please enter a valid email address');
            return;
        }

        try {
            setUpdating(true);
            setEmailError('');
            setEmailSuccess('');

            const token = localStorage.getItem('authToken');

            const response = await fetch(`${API_BASE_URL}/employee/my-profile`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email })
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setEmailSuccess('Email updated successfully!');
                setProfile(prev => ({ ...prev, email }));

                // Update localStorage userData
                const userData = JSON.parse(localStorage.getItem('userData') || '{}');
                userData.email = email;
                localStorage.setItem('userData', JSON.stringify(userData));

                setTimeout(() => setEmailSuccess(''), 3000);
            } else {
                setEmailError(data.message || 'Failed to update email');
            }
        } catch (error) {
            console.error('Error updating email:', error);
            setEmailError('Failed to update email. Please try again.');
        } finally {
            setUpdating(false);
        }
    };

    const changePassword = async () => {
        // Validation
        if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
            setPasswordError('All password fields are required');
            return;
        }

        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            setPasswordError('New password and confirm password do not match');
            return;
        }

        if (passwordForm.newPassword.length < 6) {
            setPasswordError('New password must be at least 6 characters long');
            return;
        }

        if (passwordForm.currentPassword === passwordForm.newPassword) {
            setPasswordError('New password must be different from current password');
            return;
        }

        try {
            setChangingPassword(true);
            setPasswordError('');
            setPasswordSuccess('');

            const token = localStorage.getItem('authToken');

            const response = await fetch(`${API_BASE_URL}/employee/change-password`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(passwordForm)
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setPasswordSuccess('Password changed successfully!');
                setPasswordForm({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });
                setShowPasswordForm(false);
                setTimeout(() => setPasswordSuccess(''), 3000);
            } else {
                setPasswordError(data.message || 'Failed to change password');
            }
        } catch (error) {
            console.error('Error changing password:', error);
            setPasswordError('Failed to change password. Please try again.');
        } finally {
            setChangingPassword(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                <span className="ml-2">Loading profile...</span>
            </div>
        );
    }

    if (!profile) {
        return (
            <div className="p-6 text-center">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-2" />
                <h3 className="font-medium text-gray-900 mb-1">Profile not found</h3>
                <p className="text-gray-600 text-sm">Unable to load your profile information.</p>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
                <p className="text-gray-600">Manage your personal information and account settings</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Profile Picture & Basic Info */}
                <div className="lg:col-span-1">
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <div className="text-center">
                            <div className="relative inline-block">
                                <div className="w-24 h-24 bg-gradient-to-br from-teal-400 to-blue-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                                    {profile.name ? profile.name.charAt(0).toUpperCase() : 'U'}
                                </div>
                                <button
                                    className="absolute -bottom-1 -right-1 bg-white border border-gray-300 rounded-full p-2 hover:bg-gray-50 transition-colors"
                                    title="Change profile picture (Coming soon)"
                                    disabled
                                >
                                    <Camera className="w-4 h-4 text-gray-600" />
                                </button>
                            </div>

                            <h2 className="mt-4 text-xl font-semibold text-gray-900">{profile.name}</h2>
                            <div className="mt-2 flex items-center justify-center gap-2">
                                <Badge className="w-4 h-4 text-green-600" />
                                <span className="text-sm text-green-600 font-medium capitalize">{profile.user_type}</span>
                            </div>
                        </div>

                        <div className="mt-6 space-y-4">
                            <div className="flex items-center gap-3 text-sm">
                                <Phone className="w-4 h-4 text-gray-400" />
                                <span className="text-gray-600">+91 {profile.whatsapp_no}</span>
                            </div>

                            <div className="flex items-center gap-3 text-sm">
                                <Building className="w-4 h-4 text-gray-400" />
                                <span className="text-gray-600">{profile.company_name}</span>
                            </div>

                            <div className="flex items-center gap-3 text-sm">
                                <Calendar className="w-4 h-4 text-gray-400" />
                                <span className="text-gray-600">Since {formatDate(profile.created_at)}</span>
                            </div>

                            <div className="flex items-center gap-3 text-sm">
                                <Clock className="w-4 h-4 text-gray-400" />
                                <span className="text-gray-600">
                                    Access: {formatDate(profile.start_date)} to {formatDate(profile.end_date)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Profile Information & Settings */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Email Section */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <Mail className="w-5 h-5 text-gray-500" />
                            <h3 className="text-lg font-semibold text-gray-900">Email Address</h3>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Email Address {!profile.email && <span className="text-red-500">*</span>}
                                </label>
                                <div className="flex gap-3">
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            setEmailError('');
                                            setEmailSuccess('');
                                        }}
                                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                        placeholder={profile.email ? "Update your email" : "Enter your email address"}
                                    />
                                    <button
                                        onClick={updateEmail}
                                        disabled={updating || email === profile.email}
                                        className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {updating ? (
                                            <RefreshCw className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Save className="w-4 h-4" />
                                        )}
                                        {updating ? 'Updating...' : 'Update'}
                                    </button>
                                </div>

                                {!profile.email && (
                                    <p className="text-sm text-amber-600 mt-1">
                                        Please provide your email address for important notifications
                                    </p>
                                )}

                                {emailError && (
                                    <div className="flex items-center gap-2 text-red-600 text-sm mt-2">
                                        <AlertCircle className="w-4 h-4" />
                                        {emailError}
                                    </div>
                                )}

                                {emailSuccess && (
                                    <div className="flex items-center gap-2 text-green-600 text-sm mt-2">
                                        <CheckCircle className="w-4 h-4" />
                                        {emailSuccess}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Password Section */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <Shield className="w-5 h-5 text-gray-500" />
                                <h3 className="text-lg font-semibold text-gray-900">Security</h3>
                            </div>

                            {!showPasswordForm && (
                                <button
                                    onClick={() => setShowPasswordForm(true)}
                                    className="flex items-center gap-2 text-teal-600 hover:text-teal-700 text-sm font-medium"
                                >
                                    <Edit className="w-4 h-4" />
                                    Change Password
                                </button>
                            )}
                        </div>

                        {!showPasswordForm ? (
                            <div className="text-sm text-gray-600">
                                <p>Your password was last updated on {formatDate(profile.updated_at)}</p>
                                <p className="mt-1">Click "Change Password" to update your password</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Current Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPasswords.current ? 'text' : 'password'}
                                            value={passwordForm.currentPassword}
                                            onChange={(e) => {
                                                setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }));
                                                setPasswordError('');
                                            }}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                            placeholder="Enter current password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        New Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPasswords.new ? 'text' : 'password'}
                                            value={passwordForm.newPassword}
                                            onChange={(e) => {
                                                setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }));
                                                setPasswordError('');
                                            }}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                            placeholder="Enter new password (min 6 characters)"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Confirm New Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPasswords.confirm ? 'text' : 'password'}
                                            value={passwordForm.confirmPassword}
                                            onChange={(e) => {
                                                setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }));
                                                setPasswordError('');
                                            }}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                            placeholder="Confirm new password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>

                                {passwordError && (
                                    <div className="flex items-center gap-2 text-red-600 text-sm">
                                        <AlertCircle className="w-4 h-4" />
                                        {passwordError}
                                    </div>
                                )}

                                {passwordSuccess && (
                                    <div className="flex items-center gap-2 text-green-600 text-sm">
                                        <CheckCircle className="w-4 h-4" />
                                        {passwordSuccess}
                                    </div>
                                )}

                                <div className="flex gap-3 pt-4">
                                    <button
                                        onClick={changePassword}
                                        disabled={changingPassword}
                                        className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {changingPassword ? (
                                            <RefreshCw className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Save className="w-4 h-4" />
                                        )}
                                        {changingPassword ? 'Changing...' : 'Change Password'}
                                    </button>

                                    <button
                                        onClick={() => {
                                            setShowPasswordForm(false);
                                            setPasswordForm({
                                                currentPassword: '',
                                                newPassword: '',
                                                confirmPassword: ''
                                            });
                                            setPasswordError('');
                                            setPasswordSuccess('');
                                        }}
                                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Account Information */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="text-gray-600">User ID:</span>
                                <span className="ml-2 font-medium">#{profile.id}</span>
                            </div>
                            <div>
                                <span className="text-gray-600">Status:</span>
                                <span className="ml-2 font-medium capitalize text-green-600">{profile.status}</span>
                            </div>
                            <div>
                                <span className="text-gray-600">Company ID:</span>
                                <span className="ml-2 font-medium">{profile.company_id}</span>
                            </div>
                            <div>
                                <span className="text-gray-600">Last Updated:</span>
                                <span className="ml-2 font-medium">{formatDate(profile.updated_at)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmployeeProfile;